import React from 'react'

import { Button, type ButtonProps } from '@/components/atoms/Button/Button.tsx'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  type DialogProps,
  DialogTitle,
  DialogTrigger,
} from '@/components/atoms/Dialog/Dialog.tsx'
import { cn } from '@/lib/utils.ts'

export interface ConfirmDialogLabels {
  trigger: string
  title: string
  description?: string
  confirm: string
  cancel: string
}

export interface ConfirmDialogState {
  working?: boolean
  disabled?: boolean
  onConfirmDisabled?: boolean
}

export interface ConfirmDialogAction {
  onConfirm: () => void
}

export interface ConfirmDialogProps
  extends React.HtmlHTMLAttributes<HTMLDivElement>, ConfirmDialogState, ConfirmDialogAction {
  variant?: ButtonProps['variant']
  triggerClassName?: string
  labels: ConfirmDialogLabels
  onOpenChange?: DialogProps['onOpenChange']
}

export const ConfirmDialog = React.forwardRef<HTMLDivElement, ConfirmDialogProps>(
  (
    {
      className,
      variant,
      triggerClassName,
      working = false,
      disabled = false,
      labels,
      onOpenChange,
      onConfirmDisabled,
      onConfirm,
      children,
      ...props
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false)

    return (
      <Dialog
        open={open}
        onOpenChange={(value) => {
          setOpen(value)
          onOpenChange?.(value)
        }}>
        <DialogTrigger
          asChild
          onClick={(event) => {
            event.stopPropagation()
          }}>
          <Button className={cn('py-1.5', triggerClassName)} variant={variant} loading={working} disabled={disabled}>
            {labels.trigger}
          </Button>
        </DialogTrigger>
        <DialogContent className={cn(className)} ref={ref} {...props}>
          <DialogHeader>
            <DialogTitle>{labels.title}</DialogTitle>
            {labels.description && (
              <DialogDescription
                dangerouslySetInnerHTML={{
                  __html: labels.description,
                }}
              />
            )}
          </DialogHeader>
          {children}
          <DialogFooter>
            <Button
              className="py-1.5"
              onClick={(event) => {
                setOpen(false)
                event.stopPropagation()
              }}>
              {labels.cancel}
            </Button>
            <Button
              className="py-1.5"
              disabled={onConfirmDisabled}
              onClick={(event) => {
                onConfirm()
                setOpen(false)
                event.stopPropagation()
              }}>
              {labels.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }
)
